import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from "react";
import {
  AggregateContractEntity,
  AggregateOrganization,
  AggregateHospital,
  AggregateServicingProvider,
  FiltersAction,
  MarketAlternative,
  ProviderHubContractEntities,
  ProviderHubContractEntityFiltersRequestModel,
  ProviderHubContractEntitySortColumn,
  ProviderHubOrganizationSortColumn,
  ProviderHubFilters,
  ProviderHubFiltersRequestModel,
  ProviderHubOrganizations,
  ProviderHubOrganizationsFiltersRequestModel,
  ProviderHubHospitals,
  ProviderHubHospitalsFiltersRequestModel,
  ProviderHubHospitalSortColumn,
  ProviderHubServicingProviders,
  ProviderHubSortColumn,
  ProviderHubViewEnum,
  ProviderSearchByEnum,
  ProviderViewByEnum,
  ServicingAccessPoint,
  ClaimsVolumeFilterFieldKey,
  YesNoInsufficientEnum,
  GapImpactPaginationResult,
} from "@app/projects/network-assessment/provider-hub/interfaces";
import { MembershipType } from "@app/exports/enumerations";
import {
  ContractEntitySubTypeEnum,
  LocationConfidenceScoresLabelEnum,
  ProjectRunConfigurationType,
  ProjectRunStatusEnum,
  ProviderActionStatusEnum,
  RegionSetStatusType
} from "@app/projects/enumerators";
import ProviderHubService from "@app/projects/network-assessment/provider-hub/providerHubService";
import ProjectService from "@app/projects/projectService";
import AvailableRegionStatusesModel from "@app/projects/availableRegionStatusesModel";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { contractEntityProfilePath, hospitalProfilePath, organizationProfilePath, providerProfilePath } from "@app/projects/network-assessment/provider-hub/providerHubProfilePaths";
import { buildAskQuinnContractEntityContext, buildAskQuinnProviderContext, formatSpecialtyWithCode, getContractEntitySubtype, getNetworkIntegrityGhostProviderLobLabel } from "@app/projects/network-assessment/provider-hub/utility";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { ProviderActionStatusEnumToDisplay } from "@app/projects/utility";
import { outOfNetworkProviderNetworkTypes, ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { ProviderProfileReferrerEnum } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileReferrerEnum";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { getSelectionText } from "@app/shared/utility/getSelectionText";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import ProviderHubCriticalityService from "@app/projects/network-assessment/provider-hub/providerHubCriticalityService";
import { useTopConditions } from "@app/projects/network-assessment/provider-hub/hooks/useTopConditions";
import { useTopPrescriptions } from "@app/projects/network-assessment/provider-hub/hooks/useTopPrescriptions";
import { useTopProcedures } from "@app/projects/network-assessment/provider-hub/hooks/useTopProcedures";


import { SERVICE_AREA_STATUS_DISPLAY_ORDER } from "@app/projects/network-assessment/serviceAreaStatusConstants";
import { CLAIMS_VOLUME_BREAKDOWN_QUERY_KEY_PREFIX } from "@app/projects/network-assessment/provider-hub/pci-dashboard/hooks/useClaimsVolumeBreakdown";
import { EFFECTIVENESS_EFFICIENCY_BREAKDOWN_QUERY_KEY_PREFIX } from "@app/projects/network-assessment/provider-hub/pci-dashboard/hooks/useEffectivenessEfficiencyBreakdown";
import { READY_FOR_REVIEW_QUERY_KEY_PREFIX } from "@app/projects/network-assessment/provider-hub/pci-dashboard/hooks/useReadyForReview";

interface ProviderHubContextProps {
  accessPointsPage: number;
  accessPointsPageSize: number;
  activeServiceAreaStatusFilters: Set<RegionSetStatusType>;
  adequacyCriticalityLoading: boolean;
  adequacyProviderScore: number | null;
  allOptionsText: string;
  appliedProviderFilters: ProviderHubFiltersRequestModel | null;
  defaultAccessPointsRangeMax: number;
  defaultAccessPointsRangeMin: number;
  defaultCareJourneyCostRangeMin: number;
  defaultCareJourneyCostRangeMax: number;
  defaultCareJourneyQualityRangeMin: number;
  defaultCareJourneyQualityRangeMax: number;
  defaultCloseGapsRangeMax: number;
  defaultCloseGapsRangeMin: number;
  defaultEffectivenessRangeMin: number;
  defaultEffectivenessRangeMax: number;
  defaultEfficiencyRangeMin: number;
  defaultEfficiencyRangeMax: number;
  defaultMotiveScoreRangeMin: number;
  defaultMotiveScoreRangeMax: number;
  defaultExcessAddressesRangeMin: number;
  defaultExcessAddressesRangeMax: number;
  defaultExcessSpecialtiesRangeMin: number;
  defaultExcessSpecialtiesRangeMax: number;
  defaultServicingProviderCountMax: number;
  defaultServicingProviderCountMin: number;
  contractEntityOptions: DropdownItem<ContractEntitySubTypeEnum>[];
  enableProvidersViewBackButton: boolean;
  errorLoadingContractEntity: boolean;
  errorLoadingHospital: boolean;
  errorLoadingOrganization: boolean;
  errorLoadingProvider: boolean;
  errorLoadingTopConditions: boolean;
  errorLoadingTopPrescriptions: boolean;
  errorLoadingTopProcedures: boolean;
  filterState: ProviderHubFilters;
  filtersChangedSinceLastSearch: boolean;
  goBackFromProfile: () => void;
  handleResetAll: () => void;
  handleSearchClick: () => void;
  hasFilteredCriteriaFailedToLoad: boolean;
  initialFilterState: ProviderHubFilters;
  isLoadingTopConditions: boolean;
  isLoadingTopPrescriptions: boolean;
  isLoadingTopProcedures: boolean;
  isProjectReadOnly: boolean;
  isProviderHubError: boolean;
  isProviderHubMissingData: boolean;
  isProviderHubProcessing: boolean;
  isNiMarketProviderFailed: boolean;
  isNiMarketProviderProcessing: boolean;
  isRetryingIngestion: boolean;
  isScenarioModelingError: boolean;
  isScenarioModelingProcessing: boolean;
  isSearchEnabled: boolean;
  loading: boolean;
  locationConfidenceOptions: DropdownItem<string>[];
  marketAlternatives: MarketAlternative[];
  marketAlternativesError: boolean;
  marketAlternativesLoading: boolean;
  navigateToContractEntityProfile: (contractEntity: AggregateContractEntity, referrer: ProviderProfileReferrerEnum) => void;
  navigateToHospitalProfile: (hospital: AggregateHospital, referrer: ProviderProfileReferrerEnum) => void;
  navigateToOrganizationProfile: (organization: AggregateOrganization, contractEntity: AggregateContractEntity, referrer: ProviderProfileReferrerEnum) => void;
  navigateToProviderProfile: (provider: AggregateServicingProvider, referrer: ProviderProfileReferrerEnum) => void;
  networkIntegrityGhostProviderLobLabel: string | null;
  pagedAccessPoints: ServicingAccessPoint[];
  pageIndex: number;
  pageSize: number;
  pciClaimsOptions: DropdownItem<boolean | null>[];
  pciVolumeOptions: DropdownItem<string>[];
  providerHubServicingProviders: GapImpactPaginationResult<ProviderHubServicingProviders>;
  providerHubContractEntities: GapImpactPaginationResult<ProviderHubContractEntities>;
  providerHubOrganizations: GapImpactPaginationResult<ProviderHubOrganizations>;
  providerHubHospitals: GapImpactPaginationResult<ProviderHubHospitals>;
  providerNetworkTypeOptions: NestingParentDropdownItem[];
  providerActionStatusOptions: DropdownItem<ProviderActionStatusEnum>[];
  referrer: ProviderProfileReferrerEnum;
  resetToFirstPage: boolean;
  retryIngestion: () => Promise<void>;
  serviceAreaLoadFailed: boolean;
  serviceAreaOptions: NestingParentDropdownItem[];
  serviceAreaSelectionText: string;
  serviceAreaStatusFilters: { key: string; displayValue: string }[];
  serviceAreaMatchFilter: (child: DropdownItem<string>, activeKeys: string[]) => boolean;
  selectedContractEntity: AggregateContractEntity;
  selectedProvider: AggregateServicingProvider;
  selectedOrganization: AggregateOrganization;
  selectedHospital: AggregateHospital;
  setAccessPointsPage: React.Dispatch<React.SetStateAction<number>>;
  setAccessPointsPageSize: React.Dispatch<React.SetStateAction<number>>;
  setActiveServiceAreaStatusFilters: React.Dispatch<React.SetStateAction<Set<RegionSetStatusType>>>;
  setErrorLoadingContractEntity: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorLoadingHospital: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorLoadingOrganization: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorLoadingProvider: React.Dispatch<React.SetStateAction<boolean>>;
  setFilter: <T extends keyof ProviderHubFilters>(key: T, value: ProviderHubFilters[T]) => void;
  setMarketAlternatives: React.Dispatch<React.SetStateAction<MarketAlternative[]>>;
  setMarketAlternativesError: React.Dispatch<React.SetStateAction<boolean>>;
  setMarketAlternativesLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setPagedAccessPoints: React.Dispatch<React.SetStateAction<ServicingAccessPoint[]>>;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setProviderHubServicingProviders: React.Dispatch<React.SetStateAction<GapImpactPaginationResult<ProviderHubServicingProviders>>>;
  setProviderHubContractEntities: React.Dispatch<React.SetStateAction<GapImpactPaginationResult<ProviderHubContractEntities>>>;
  setProviderHubOrganizations: React.Dispatch<React.SetStateAction<GapImpactPaginationResult<ProviderHubOrganizations>>>;
  setReferrer: React.Dispatch<React.SetStateAction<ProviderProfileReferrerEnum>>;
  setResetToFirstPage: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedProvider: React.Dispatch<React.SetStateAction<AggregateServicingProvider>>;
  setSelectedOrganization: React.Dispatch<React.SetStateAction<AggregateOrganization>>;
  setSelectedHospital: React.Dispatch<React.SetStateAction<AggregateHospital>>;
  setSelectedContractEntity: React.Dispatch<React.SetStateAction<AggregateContractEntity>>;
  setServiceAreaOptions: React.Dispatch<React.SetStateAction<NestingParentDropdownItem[]>>;
  setShowMembershipTypeDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  setSortColumnAndOrder: React.Dispatch<React.SetStateAction<ProviderHubSortColumn>>;
  setSortContractEntityColumnAndOrder: React.Dispatch<React.SetStateAction<ProviderHubContractEntitySortColumn>>;
  specialtyGroupsLoadFailed: boolean;
  setSpecialtyOptions: React.Dispatch<React.SetStateAction<DropdownItem<string>[]>>;
  setTotalAccessPointsPages: React.Dispatch<React.SetStateAction<number>>;
  setView: React.Dispatch<React.SetStateAction<ProviderHubViewEnum>>;
  showAdvancedFiltersIndicator: boolean;
  showCareJourneyFilters: boolean;
  showNetworkIntegrityAttestationBasedAlertsFilter: boolean;
  showNetworkIntegrityCriticalAlertsFilter: boolean;
  showNetworkIntegrityFilters: boolean;
  showMembershipTypeDropdown: boolean;
  showLocationConfidenceFilter: boolean;
  showAccessPointLocationConfidence: boolean;
  showMotiveScoreFilters: boolean;
  showPciFilters: boolean;
  showFilterToggle: boolean;
  showTriCareFilters: boolean;
  showVaFilters: boolean;
  sortColumnAndOrder: ProviderHubSortColumn;
  sortContractEntityColumnAndOrder: ProviderHubContractEntitySortColumn;
  specialtygruy: DropdownItem<string>[];
  specialtyOptions: DropdownItem<string>[];
  specialtySelectionText: string;
  statusToRegionSet: Map<RegionSetStatusType, Set<number>>;
  topConditionOptions: DropdownItem<string>[];
  topPrescriptionOptions: DropdownItem<string>[];
  topProcedureOptions: DropdownItem<string>[];
  totalAccessPointsPages: number;
  vaNewPatientIndicatorSelectionText: string;
  view: ProviderHubViewEnum;
  viewNetworkExpansionOpportunityProviders: (specialtyGroupCode?: string) => Promise<void>,
  viewProvidersFilteredByClaimsVolume: (filterFieldKey: ClaimsVolumeFilterFieldKey, claimsVolumeLabel: ClaimsActivityVolumeLabelEnum) => Promise<void>;
  viewProvidersFilteredByScore: (filterFieldKey: "effectiveness" | "efficiency", score: number) => Promise<void>,
  viewUnderperformingProviders: (specialtyGroupCode?: string) => Promise<void>,
  membershipAliases?: {
    primary: string;
    secondary: string;
  };
}

interface ProviderHubRouteMatchParams {
  providerKey?: string;
  contractEntityId?: string;
  organizationNpi?: string;
  ccn?: string;
  runConfigurationId?: string;
}

// Carried in the history entry's state (part of the URL/history, not an in-memory stack) so a profile can
// tell whether it was reached by an in-app drill-down (Go Back = browser back) versus opened directly via a
// deep link/new tab (Go Back has nowhere to pop, so fall back to the Provider Hub landing page).
interface ProviderHubNavigationState {
  providerHubInternalNav?: boolean;
  referrer?: ProviderProfileReferrerEnum;
}

/**
 * Filter values as they stood immediately before a PCI Dashboard drill-in overwrote them. Held in state
 * (see `preDrillInFiltersSnapshot`) to support reverting drill-ins.
 */
interface PreDrillInFiltersSnapshot {
  /** filterState values before drill-in. Can be the full filterState, or just the filters overwritten by the drill-in. */
  filterOverrides: Partial<ProviderHubFilters>;
  /** The filters that were last applied before the drill-in. */
  appliedProviderFilters: ProviderHubFiltersRequestModel | null;
}

const PCI_DASHBOARD_SUBROUTE = "/provider-claims-insights";

const ProviderHubContext = createContext<Partial<ProviderHubContextProps>>({});

const ProviderHubContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const routeMatch = useRouteMatch<ProviderHubRouteMatchParams>();
  const history = useHistory();
  const location = useLocation<ProviderHubNavigationState>();
  const projectContext = useContext(CountyProjectContext);
  const queryClient = useQueryClient();
  const providerHubService = useRef(new ProviderHubService()).current;
  const providerHubCriticalityService = useRef(new ProviderHubCriticalityService()).current;
  const projectService = useRef(new ProjectService()).current;
  const userContext = useContext(UserContext);
  const featureFlagContext = useContext(FeatureFlagContext);
  const allOptionsText = "All";
  const defaultPageSize = 50;

  const [providerHubServicingProviders, setProviderHubServicingProviders] = useState<GapImpactPaginationResult<ProviderHubServicingProviders>>({ items: [], totalCount: 0 });
  const [providerHubContractEntities, setProviderHubContractEntities] = useState<GapImpactPaginationResult<ProviderHubContractEntities>>({ items: [], totalCount: 0 });
  const [providerHubOrganizations, setProviderHubOrganizations] = useState<GapImpactPaginationResult<ProviderHubOrganizations>>({ items: [], totalCount: 0 });
  const [providerHubHospitals, setProviderHubHospitals] = useState<GapImpactPaginationResult<ProviderHubHospitals>>({ items: [], totalCount: 0 });

  // Had to move this to before using it when setting defaultProviderNetworkTypes
  /* oxlint-disable sort-keys */
  const providerNetworkTypeOptions: NestingParentDropdownItem[] = [
    { key: ProviderNetworkTypeEnum.Network, name: 'In-Network', checkboxStatus: CheckboxStateEnum.Unchecked, children: [] },
    {
      key: 'Out-of-Network', name: 'Out-of-Network', checkboxStatus: CheckboxStateEnum.Unchecked, children: [
        { key: ProviderNetworkTypeEnum.Prospect, name: 'Prospect' },
        { key: ProviderNetworkTypeEnum.Market, name: 'Market' },
        { key: ProviderNetworkTypeEnum.NetNew, name: 'Net New' }
      ]
    }
  ];
  /* oxlint-enable sort-keys */

  const getFilteredProviderActionStatusesBasedOnRole = useMemo<ProviderActionStatusEnum[]>(() => {
    if (!(userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN) || userContext.roles.includes(Roles.TERM_PROVIDER_VIEWER))) {
      return Object.values(ProviderActionStatusEnum).filter(status => status !== ProviderActionStatusEnum.PartiallyTerminated && status !== ProviderActionStatusEnum.FullyTerminated);
    }
    return Object.values(ProviderActionStatusEnum);
  }, [userContext.roles]);

  const providerActionStatusOptions: DropdownItem<ProviderActionStatusEnum | null>[] = [
    { key: null, name: 'No Actions Applied' },
    ...getFilteredProviderActionStatusesBasedOnRole
      .filter((value) => value !== ProviderActionStatusEnum.Unknown)
      .map((value) => ({
        key: value,
        name: ProviderActionStatusEnumToDisplay(value)
      }))
  ];

  const contractEntityOptions: DropdownItem<ContractEntitySubTypeEnum | null>[] = [
    ...Object.values(ContractEntitySubTypeEnum).map((value) => ({
      key: value,
      name: getContractEntitySubtype(value)
    }))
  ];

  // Values related to the filters (source data and defaults)
  const [showMembershipTypeDropdown, setShowMembershipTypeDropdown] = useState<boolean>(false);
  const [activeServiceAreaStatusFilters, setActiveServiceAreaStatusFilters] = useState<Set<RegionSetStatusType>>(
    new Set()
  );
  const [statusToRegionSet, setStatusToRegionSet] = useState<Map<RegionSetStatusType, Set<number>>>(new Map());
  const [serviceAreaOptions, setServiceAreaOptions] = useState<NestingParentDropdownItem[]>([]);
  const [specialtyOptions, setSpecialtyOptions] = useState<DropdownItem<string>[]>([]);
  const [defaultCloseGapsRangeMin] = useState<number>(0);
  const [defaultCloseGapsRangeMax, setDefaultCloseGapsRangeMax] = useState<number>(500000); // set high just in case defaults are use to fetch max from API
  const [defaultAccessPointsRangeMin] = useState<number>(1);
  const [defaultAccessPointsRangeMax, setDefaultAccessPointsRangeMax] = useState<number>(50000); // set high just in case defaults are use to fetch max from API
  const defaultCareJourneyCostRangeMin = 1;
  const defaultCareJourneyCostRangeMax = 5;
  const defaultCareJourneyQualityRangeMin = 1;
  const defaultCareJourneyQualityRangeMax = 5;
  const defaultEffectivenessRangeMin = 1;
  const defaultEffectivenessRangeMax = 5;
  const defaultEfficiencyRangeMin = 1;
  const defaultEfficiencyRangeMax = 5;
  const defaultMotiveScoreRangeMin = 1;
  const defaultMotiveScoreRangeMax = 5;
  const defaultExcessAddressesRangeMin = 0;
  const defaultExcessAddressesRangeMax = 21;
  const defaultExcessSpecialtiesRangeMin = 0;
  const defaultExcessSpecialtiesRangeMax = 21;
  const defaultServicingProviderCountMin = 0;
  const [defaultServicingProviderCountMax, setDefaultServicingProviderCountMax] = useState<number>(500000);
  const [serviceAreaLoadFailed, setServiceAreaLoadFailed] = useState(false);
  const [specialtyGroupsLoadFailed, setSpecialtyGroupsLoadFailed] = useState(false);
  const [maxProviderMetricLoadFailed, setMaxProviderMetricLoadFailed] = useState(false);

  const hasFilteredCriteriaFailedToLoad = serviceAreaLoadFailed || specialtyGroupsLoadFailed || maxProviderMetricLoadFailed;

  /* Available options for PCI dropdown filters */
  const pciVolumeOptions: DropdownItem<string>[] = Object.entries(ClaimsActivityVolumeLabelEnum)
    .filter(([key]) => key !== ClaimsActivityVolumeLabelEnum.Total)
    .map(([key, value]) => ({
      key: key,
      name: value,
    }));

  const pciClaimsOptions: DropdownItem<boolean | null>[] = Object.keys(YesNoInsufficientEnum)
    .map(key => ({ key: key === "Yes" ? true : key === "No" ? false : null, name: key }));

  const locationConfidenceOptions: DropdownItem<string>[] = Object.entries(LocationConfidenceScoresLabelEnum)
    .map(([k, v]) => {
      const key = v === LocationConfidenceScoresLabelEnum.Unknown ? null : k;
      let name = v.toString();

      if (v === LocationConfidenceScoresLabelEnum.Unknown) name = "No Score Available";
      if (v === LocationConfidenceScoresLabelEnum.VeryLow) name = "Very Low";
      return { key, name }
    });

  const networkIntegrityGhostProviderLobLabel = getNetworkIntegrityGhostProviderLobLabel(
    projectContext?.project?.networkTaxonomy?.lineOfBusiness,
    projectContext?.project?.networkTaxonomy?.lineOfBusinessType
  );

  // Used for pagination and sorting in the grid
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const [resetToFirstPage, setResetToFirstPage] = useState<boolean>(false);
  const [sortColumnAndOrder, setSortColumnAndOrder] = useState<ProviderHubSortColumn>(ProviderHubSortColumn.NameAscending);
  const [sortContractEntityColumnAndOrder, setSortContractEntityColumnAndOrder] = useState<ProviderHubContractEntitySortColumn>(ProviderHubContractEntitySortColumn.NameAscending);

  // Use for pagination in the Access Point cards view
  const [accessPointsPage, setAccessPointsPage] = useState<number>(1);
  const [accessPointsPageSize, setAccessPointsPageSize] = useState<number>(25);
  const [totalAccessPointsPages, setTotalAccessPointsPages] = useState<number>(1);
  const [pagedAccessPoints, setPagedAccessPoints] = useState<ServicingAccessPoint[]>([]);

  // Values related to processing state
  const [isProviderHubProcessing, setIsProviderHubProcessing] = useState<boolean>(false);
  const [isProviderHubError, setIsProviderHubError] = useState<boolean>(false);
  const [isNiMarketProviderProcessing, setIsNiMarketProviderProcessing] = useState<boolean>(false);
  const [isNiMarketProviderFailed, setIsNiMarketProviderFailed] = useState<boolean>(false);
  const [isScenarioModelingProcessing, setIsScenarioModelingProcessing] = useState<boolean>(false);
  const [isProviderHubMissingData, setIsProviderHubMissingData] = useState(false);
  const [isRetryingIngestion, setIsRetryingIngestion] = useState<boolean>(false);

  /** Determines if Provider Hub processing has completed, has data, and is ready to be searched. */
  const isSearchEnabled = !isProviderHubProcessing &&
    !isProviderHubError &&
    !isProviderHubMissingData &&
    !hasFilteredCriteriaFailedToLoad;

  // Miscellaneous
  // The URL is the source of truth for which profile is shown. Pick the initial view from whichever profile
  // param is present; the URL-sync effect below hydrates the matching profile and keeps view in sync as the
  // user navigates (push/pop). Organization is checked before Contract Entity because the org route carries
  // both organizationNpi and contractEntityId.
  const deepLinkView = !!routeMatch.params.providerKey
    ? ProviderHubViewEnum.ProviderProfile
    : !!routeMatch.params.ccn
      ? ProviderHubViewEnum.HospitalProfile
      : !!routeMatch.params.organizationNpi
        ? ProviderHubViewEnum.OrganizationProfile
        : !!routeMatch.params.contractEntityId
          ? ProviderHubViewEnum.ContractEntityProfile
          : null;
  const [view, setView] = useState(deepLinkView ?? ProviderHubViewEnum.EmptyState);
  const [loading, setLoading] = useState<boolean>(deepLinkView !== null);
  const [selectedProvider, setSelectedProvider] = useState<AggregateServicingProvider>();
  const [selectedContractEntity, setSelectedContractEntity] = useState<AggregateContractEntity>();
  const [selectedHospital, setSelectedHospital] = useState<AggregateHospital>();
  const [selectedOrganization, setSelectedOrganization] = useState<AggregateOrganization>();
  const [errorLoadingProvider, setErrorLoadingProvider] = useState(false);
  const [errorLoadingContractEntity, setErrorLoadingContractEntity] = useState(false);
  const [errorLoadingOrganization, setErrorLoadingOrganization] = useState(false);
  const [errorLoadingHospital, setErrorLoadingHospital] = useState(false);
  const [showLocationConfidenceFilter, setShowLocationConfidenceFilter] = useState<boolean>(false);
  const [showAccessPointLocationConfidence, setShowAccessPointLocationConfidence] = useState<boolean>(false);
  const [showMotiveScoreFilters, setShowMotiveScoreFilters] = useState<boolean>(false);
  const [showNetworkIntegrityAttestationBasedAlertsFilter, setShowNetworkIntegrityAttestationBasedAlertsFilter] = useState<boolean>(false);
  const [showNetworkIntegrityCriticalAlertsFilter, setShowNetworkIntegrityCriticalAlertsFilter] = useState<boolean>(false);
  const [showNetworkIntegrityFilters, setShowNetworkIntegrityFilters] = useState<boolean>(false);
  const [showPciFilters, setShowPciFilters] = useState<boolean>(false);
  const [showTriCareFilters, setShowTriCareFilters] = useState<boolean>(false);
  const [showCareJourneyFilters, setShowCareJourneyFilters] = useState<boolean>(false);
  const [showVaFilters, setShowVaFilters] = useState<boolean>(false);
  const [referrer, setReferrer] = useState<ProviderProfileReferrerEnum>(ProviderProfileReferrerEnum.ProviderHubLandingPage);
  const [adequacyProviderScore, setAdequacyProviderScore] = useState<number | null>(null);
  const [adequacyCriticalityLoading, setAdequacyCriticalityLoading] = useState<boolean>(false);
  const [marketAlternatives, setMarketAlternatives] = useState<MarketAlternative[]>([]);
  const [marketAlternativesLoading, setMarketAlternativesLoading] = useState(false);
  const [marketAlternativesError, setMarketAlternativesError] = useState<boolean>(false);

  // Dynamically loaded filter options
  const enableTopProceduresConditionsAndPrescriptions = isSearchEnabled && showPciFilters
    && featureFlagContext.enabledFeatureFlags.has(Features.ENABLE_PROVIDER_HUB_PCI_FILTER_SEARCH);
  const {
    isLoading: isLoadingTopProcedures,
    isError: errorLoadingTopProcedures,
    data: topProcedureOptions,
  } = useTopProcedures(
    projectContext?.project?.projectId,
    projectContext?.runConfiguration?.projectRunConfigurationId,
    enableTopProceduresConditionsAndPrescriptions
  );
  const {
    isLoading: isLoadingTopConditions,
    isError: errorLoadingTopConditions,
    data: topConditionOptions,
  } = useTopConditions(
    projectContext?.project?.projectId,
    projectContext?.runConfiguration?.projectRunConfigurationId,
    enableTopProceduresConditionsAndPrescriptions
  );
  const {
    isLoading: isLoadingTopPrescriptions,
    isError: errorLoadingTopPrescriptions,
    data: topPrescriptionOptions,
  } = useTopPrescriptions(
    projectContext?.project?.projectId,
    projectContext?.runConfiguration?.projectRunConfigurationId,
    enableTopProceduresConditionsAndPrescriptions
  );

  /** An API ready filters model from the last time the user clicked search.
   * Used to make tanstack queries wait until user "applies" filters.
   * A value of null means that the user has not searched yet since loading the page or clicking "Reset All".
   */
  const [appliedProviderFilters, setAppliedProviderFilters] = useState<ProviderHubFiltersRequestModel | null>(null);

  useEffect(() => {
    if (!selectedProvider) {
      return;
    }
    setAdequacyProviderScore(null);
    setAdequacyCriticalityLoading(true);
    providerHubCriticalityService.getAdequacyCriticality(
      selectedProvider.projectRunConfigurationId,
      selectedProvider.servicingProviderKey
    ).then(
      (adequacyCriticality) => {
        setAdequacyProviderScore(adequacyCriticality.score ?? null);
        setAdequacyCriticalityLoading(false);
      },
      () => {
        setAdequacyCriticalityLoading(false);
      }
    );
  }, [selectedProvider]);

  useEffect(() => {
    // Single source of truth for the Ask-Quinn provider/contract-entity context. Any path that shows a
    // provider or contract entity profile sets the corresponding selected* state + view; any path that
    // leaves it nulls that state. Sync the virtual-assistant key/name off that state so the chat system
    // message is always correct, clearing the other entity type's fields in the same update so switching
    // between profiles doesn't leave stale context behind. isOpen is left untouched — it is owned by the
    // open/close callbacks.
    projectContext.setVirtualAssistantContext({
      ...buildAskQuinnProviderContext(view, selectedProvider),
      ...buildAskQuinnContractEntityContext(view, selectedContractEntity)
    });
  }, [view, selectedProvider, selectedContractEntity]);

  const [showFilterToggle, setShowFilterToggle] = useState<boolean>(false);
  /** Used to determine if the search button says "Update Results" or "Search". Should only be set if the current result set is stale. */
  const [filtersChangedSinceLastSearch, setFiltersChangedSinceLastSearch] = useState<boolean>(false);

  /**
   * Snapshot of the filters before a PCI Dashboard drill-in. Used to revert the drill-in when the user returns
   * to PCI Dashboard. Cleared if user resets filters or searches before returning (disables revert).
   */
  const [preDrillInFiltersSnapshot, setPreDrillInFiltersSnapshot] = useState<PreDrillInFiltersSnapshot | null>(null);
  const enableProvidersViewBackButton = preDrillInFiltersSnapshot !== null;

  // The last list/search view we showed at the bare /provider-hub URL. Restored when the user pops back out
  // of a profile (Go Back) so they land on the same results they drilled from. This is a single bounded
  // value, NOT a navigation stack — profile depth is driven entirely by the URL/browser history.
  const listViews = useRef<Set<ProviderHubViewEnum>>(new Set([
    ProviderHubViewEnum.EmptyState,
    ProviderHubViewEnum.Providers,
    ProviderHubViewEnum.ContractEntity,
    ProviderHubViewEnum.Organization,
    ProviderHubViewEnum.Hospital,
    ProviderHubViewEnum.ProviderClaimsInsights,
  ])).current;
  const lastListView = useRef<ProviderHubViewEnum>(ProviderHubViewEnum.EmptyState);
  useEffect(() => {
    if (listViews.has(view)) {
      lastListView.current = view;
    }
  }, [view]);

  // URL -> view/selection sync. The semantic profile URL is the source of truth for which profile is shown,
  // so this runs both on first paint (deep link / refresh / new tab) and on every push/pop (drill-down and
  // browser Back/Forward). It re-fetches a profile only when the URL identity differs from what is loaded, so
  // in-app drill-downs (which set the selected entity before pushing) don't trigger a redundant fetch, while
  // deep links / refreshes / stale forward navigations do. At the bare /provider-hub URL it restores the
  // prior list view (see lastListView) with the persisted filters/results — this is what preserves the "one
  // layer" of filter context on Go Back.
  useEffect(() => {
    const projectId = projectContext?.project?.projectId;
    const runConfigurationId = Number(routeMatch.params.runConfigurationId);
    const { ccn, contractEntityId, organizationNpi, providerKey } = routeMatch.params;

    if (!!providerKey) {
      const key = decodeURIComponent(providerKey).toUpperCase();
      setView(ProviderHubViewEnum.ProviderProfile);
      projectContext.setHideProjectHeader(true);
      if (selectedProvider?.servicingProviderKey?.toUpperCase() !== key || selectedProvider?.projectRunConfigurationId !== runConfigurationId) {
        setSelectedProvider(null);
        setLoading(true);
        providerHubService.getServicingProviderDetails(projectId, runConfigurationId, key).then(
          (provider) => { setSelectedProvider(provider); setLoading(false); setErrorLoadingProvider(false); },
          () => { setLoading(false); setErrorLoadingProvider(true); });
      }
    } else if (!!ccn) {
      const ccnValue = decodeURIComponent(ccn);
      setView(ProviderHubViewEnum.HospitalProfile);
      projectContext.setHideProjectHeader(true);
      if (selectedHospital?.ccn !== ccnValue || selectedHospital?.projectRunConfigurationId !== runConfigurationId) {
        setSelectedHospital(null);
        setLoading(true);
        providerHubService.getHospitalDetails(projectId, runConfigurationId, ccnValue).then(
          (hospital) => { setSelectedHospital(hospital); setLoading(false); setErrorLoadingHospital(false); },
          () => { setLoading(false); setErrorLoadingHospital(true); });
      }
    } else if (!!organizationNpi && !!contractEntityId) {
      const npi = decodeURIComponent(organizationNpi);
      const ceId = Number(contractEntityId);
      setView(ProviderHubViewEnum.OrganizationProfile);
      projectContext.setHideProjectHeader(true);
      if (selectedOrganization?.npi !== npi || selectedOrganization?.projectRunConfigurationId !== runConfigurationId) {
        setSelectedOrganization(null);
        setLoading(true);
        Promise.all([
          providerHubService.getOrganizationDetails(projectId, runConfigurationId, npi, ceId),
          providerHubService.getContractEntityDetails(projectId, runConfigurationId, ceId),
        ]).then(
          ([organization, contractEntity]) => {
            // The endpoint resolves with 200 + null (rather than rejecting) when the id has no match —
            // e.g. a contract entity with no primary-provider association. Treat that as the error case so
            // the profile shows the "not found" alert instead of spinning forever on a null selection.
            if (!organization || !contractEntity) {
              setLoading(false);
              setErrorLoadingOrganization(true);
              return;
            }
            setSelectedOrganization(organization);
            setSelectedContractEntity(contractEntity);
            setLoading(false);
            setErrorLoadingOrganization(false);
          },
          () => { setLoading(false); setErrorLoadingOrganization(true); });
      }
    } else if (!!contractEntityId) {
      const ceId = Number(contractEntityId);
      setView(ProviderHubViewEnum.ContractEntityProfile);
      projectContext.setHideProjectHeader(true);
      if (selectedContractEntity?.contractEntityId !== ceId || selectedContractEntity?.projectRunConfigurationId !== runConfigurationId) {
        setSelectedContractEntity(null);
        setLoading(true);
        providerHubService.getContractEntityDetails(projectId, runConfigurationId, ceId).then(
          (contractEntity) => {
            // The endpoint resolves with 200 + null (rather than rejecting) when the id has no match — e.g.
            // a contract entity with no primary-provider association. Treat that as the error case so the
            // profile shows the "not found" alert instead of spinning forever on a null selection.
            if (!contractEntity) {
              setLoading(false);
              setErrorLoadingContractEntity(true);
              return;
            }
            setSelectedContractEntity(contractEntity);
            setLoading(false);
            setErrorLoadingContractEntity(false);
          },
          () => { setLoading(false); setErrorLoadingContractEntity(true); });
      }
    } else if (!routeMatch.path?.endsWith(PCI_DASHBOARD_SUBROUTE)) {
      // Bare /provider-hub URL: popped back out of a profile. Restore the list view we drilled from; the PCI
      // pseudo-tab is owned by the routeMatch.path effect below and is intentionally left untouched here.
      setView(lastListView.current);
      projectContext.setHideProjectHeader(false);
    }
  }, [routeMatch.params.providerKey, routeMatch.params.ccn, routeMatch.params.organizationNpi, routeMatch.params.contractEntityId, routeMatch.params.runConfigurationId]);

  // Drill-down navigation. Each helper sets the selected entity (so the profile renders immediately, without a
  // round-trip) and pushes the matching semantic URL tagged as an in-app navigation. The URL-sync effect above
  // then keeps `view` in step, and Go Back / browser Back pops the entry. runConfigurationId comes off the
  // entity so a refresh of the resulting URL re-hydrates the same record.
  const navigateToProviderProfile = (provider: AggregateServicingProvider, referrer: ProviderProfileReferrerEnum) => {
    setSelectedProvider(provider);
    setReferrer(referrer);
    setErrorLoadingProvider(false);
    projectContext.setHideProjectHeader(true);
    history.push(
      providerProfilePath(projectContext?.project?.projectId, provider.projectRunConfigurationId, provider.servicingProviderKey),
      { providerHubInternalNav: true, referrer }
    );
  };

  const navigateToContractEntityProfile = (contractEntity: AggregateContractEntity, referrer: ProviderProfileReferrerEnum) => {
    setSelectedContractEntity(contractEntity);
    setReferrer(referrer);
    setErrorLoadingContractEntity(false);
    projectContext.setHideProjectHeader(true);
    history.push(
      contractEntityProfilePath(projectContext?.project?.projectId, contractEntity.projectRunConfigurationId, contractEntity.contractEntityId),
      { providerHubInternalNav: true, referrer }
    );
  };

  const navigateToOrganizationProfile = (organization: AggregateOrganization, contractEntity: AggregateContractEntity, referrer: ProviderProfileReferrerEnum) => {
    setSelectedOrganization(organization);
    setSelectedContractEntity(contractEntity);
    setReferrer(referrer);
    setErrorLoadingOrganization(false);
    projectContext.setHideProjectHeader(true);
    history.push(
      organizationProfilePath(projectContext?.project?.projectId, organization.projectRunConfigurationId, organization.npi, contractEntity.contractEntityId),
      { providerHubInternalNav: true, referrer }
    );
  };

  const navigateToHospitalProfile = (hospital: AggregateHospital, referrer: ProviderProfileReferrerEnum) => {
    setSelectedHospital(hospital);
    setReferrer(referrer);
    setErrorLoadingHospital(false);
    projectContext.setHideProjectHeader(true);
    history.push(
      hospitalProfilePath(projectContext?.project?.projectId, hospital.projectRunConfigurationId, hospital.ccn),
      { providerHubInternalNav: true, referrer }
    );
  };

  // Go Back for every profile. In-app drill-downs push a history entry tagged internal, so this is a real
  // browser Back and navigating more than one profile deep falls through to standard browser behavior. A
  // profile opened cold (deep link / new tab) has no entry to pop, so fall back to the Provider Hub landing page.
  const goBackFromProfile = () => {
    if (location.state?.providerHubInternalNav) {
      history.goBack();
    } else {
      history.replace(`/projects/${projectContext?.project?.projectId}/provider-hub`);
    }
  };

  useEffect(() => {
    if (featureFlagContext.enabledFeatureFlags.has(Features.ENABLE_SNOWFLAKE_PCI_DASHBOARD)) {
      if (routeMatch.path.endsWith(PCI_DASHBOARD_SUBROUTE)) {
        setView(ProviderHubViewEnum.ProviderClaimsInsights);
        setReferrer(ProviderProfileReferrerEnum.ProviderHubLandingPage);
        if (filterState.viewByOption !== ProviderViewByEnum.Provider || !!filterState.searchByInput || filterState.searchByOption !== ProviderSearchByEnum.Name) {
          // Filtering must always be by provider on PCI Dashboard
          setFilterWithoutMarkingAsChanged("viewByOption", ProviderViewByEnum.Provider);
          // Search by input not supported by PCI Dashboard -- clear it
          setFilterWithoutMarkingAsChanged("searchByInput", "");
          setFilterWithoutMarkingAsChanged("searchByOption", ProviderSearchByEnum.Name);

          // Results might change due to searchByInput change; doing this forces a PCI Dashboard auto-load, overwriting those previous results
          setAppliedProviderFilters(null);
        }

        if (preDrillInFiltersSnapshot) {
          revertPciDashboardDrillInFilters(preDrillInFiltersSnapshot);
        }
      } else if (view === ProviderHubViewEnum.ProviderClaimsInsights) {
        // Moving from PCI Dashboard to Provider Hub should flip to the Providers list view
        setView(ProviderHubViewEnum.Providers);
      }
    }
  }, [routeMatch.path]);

  // Auto-load results when switching to PCI Dashboard - but only if no filters are set (don't auto-apply filters set by user).
  useEffect(() => {
    if (
      view === ProviderHubViewEnum.ProviderClaimsInsights &&
      appliedProviderFilters === null &&
      isSearchEnabled &&
      // Don't auto-load until projectId and projectRunConfigurationId is set in projectContext
      !!projectContext?.project?.projectId &&
      !!projectContext?.runConfiguration?.projectRunConfigurationId
    ) {
      // Sets appliedProviderFilters (so PCI Dashboard queries load) and pre-loads providers list (to page 1) for when user switches tabs back
      refreshData(true);
    }
  }, [
    view,
    appliedProviderFilters,
    isSearchEnabled,
    projectContext?.project?.projectId,
    projectContext?.runConfiguration?.projectRunConfigurationId,
  ]);

  const initialFilterState: ProviderHubFilters = {
    accessPointCountMax: defaultAccessPointsRangeMax,
    accessPointCountMin: defaultAccessPointsRangeMin,
    careJourneyCostScore: [],
    careJourneyCostScoreMax: defaultCareJourneyCostRangeMax,
    careJourneyCostScoreMin: defaultCareJourneyCostRangeMin,
    careJourneyQualityScore: [],
    careJourneyQualityScoreMax: defaultCareJourneyQualityRangeMax,
    careJourneyQualityScoreMin: defaultCareJourneyQualityRangeMin,
    closeGapsMax: defaultCloseGapsRangeMax,
    closeGapsMin: defaultCloseGapsRangeMin,
    commercialClaimsVolume: [],
    commercialNewPatientIndicator: [],
    companionProjectRunConfigurationId: projectContext?.runConfiguration?.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental
      ? projectContext?.runConfiguration?.projectRunConfigurationId
      : projectContext?.runConfiguration?.companionProjectRunConfigurationId,
    effectiveness: [],
    effectivenessMax: defaultEffectivenessRangeMax,
    effectivenessMin: defaultEffectivenessRangeMin,
    efficiency: [],
    efficiencyMax: defaultEfficiencyRangeMax,
    efficiencyMin: defaultEfficiencyRangeMin,
    includeUnknownCareJourneyCostScore: true,
    includeUnknownCareJourneyQualityScore: true,
    includeUnknownEffectiveness: true,
    includeUnknownEfficiency: true,
    includeUnknownMotiveScore: true,
    locationConfidence: [],
    medicaidClaimsVolume: [],
    medicaidNewPatientIndicator: [],
    medicareClaimsVolume: [],
    medicareNewPatientIndicator: [],
    membershipType: projectContext?.runConfiguration?.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental
      ? MembershipType.Secondary
      : MembershipType.Primary,
    motiveScore: [],
    motiveScoreMax: defaultMotiveScoreRangeMax,
    motiveScoreMin: defaultMotiveScoreRangeMin,
    networkIntegrityAttestationBasedAlerts: [],
    networkIntegrityCriticalAlerts: [],
    networkIntegrityExcessAddressesMax: defaultExcessAddressesRangeMax,
    networkIntegrityExcessAddressesMin: defaultExcessAddressesRangeMin,
    networkIntegrityExcessSpecialtiesMax: defaultExcessSpecialtiesRangeMax,
    networkIntegrityExcessSpecialtiesMin: defaultExcessSpecialtiesRangeMin,
    networkIntegrityProjectOptionsExclusions: [],
    pageIndex: 0,
    pageSize: defaultPageSize,
    projectId: projectContext?.project?.projectId ?? null,
    projectRunConfigurationId: projectContext?.runConfiguration?.projectRunConfigurationType === ProjectRunConfigurationType.DualMembershipSupplemental
      ? projectContext?.runConfiguration?.companionProjectRunConfigurationId
      : projectContext?.runConfiguration?.projectRunConfigurationId,
    providerActionStatuses: [],
    providerNetworkTypes: [],
    regionSetIds: [],
    searchByInput: "",
    searchByOption: ProviderSearchByEnum.Name,
    servicingProviderCountMax: defaultServicingProviderCountMax,
    servicingProviderCountMin: defaultServicingProviderCountMin,
    sortColumnAndOrder: ProviderHubSortColumn.NameAscending,
    sortContractEntityColumnAndOrder: ProviderHubContractEntitySortColumn.NameAscending,
    sortHospitalColumnAndOrder: ProviderHubHospitalSortColumn.NameAscending,
    sortOrganizationColumnAndOrder: ProviderHubOrganizationSortColumn.NameAscending,
    specialtyGroupCodes: [],
    topConditions: [],
    topPrescriptions: [],
    topProcedures: [],
    totalClaimsVolume: [],
    totalNewPatientIndicator: [],
    tricareClaimsVolume: [],
    tricareNewPatientIndicator: [],
    vaClaimsVolume: [],
    vaNewPatientIndicator: [],
    viewByOption: ProviderViewByEnum.Provider
  };

  const filtersReducer = (filtersState: ProviderHubFilters, action: FiltersAction): ProviderHubFilters => {
    switch (action.type) {
      case 'SET_FILTER':
        return {
          ...filtersState,
          [action.key]: action.value,
        };
      case 'RESET_FILTERS':
        return initialFilterState;
      default:
        return filtersState;
    }
  };

  // Filter state values handled in a reducer
  const [filterState, filterStateDispatch] = useReducer(filtersReducer, initialFilterState);

  const handleResetAll = async () => {
    setPreDrillInFiltersSnapshot(null);
    const currentViewByOption = filterState.viewByOption;
    filterStateDispatch({ type: 'RESET_FILTERS' });
    setFilter("viewByOption", currentViewByOption);
    if (currentViewByOption === ProviderViewByEnum.ContractEntity) {
      setFilter("searchByOption", ProviderSearchByEnum.ContractEntity);
    }
    setFilter("closeGapsMax", defaultCloseGapsRangeMax);
    setFilter("accessPointCountMax", defaultAccessPointsRangeMax);
    setFilter("servicingProviderCountMax", defaultServicingProviderCountMax);
    setProviderHubServicingProviders({ items: [], totalCount: 0 });
    setProviderHubContractEntities({ items: [], totalCount: 0 });
    setProviderHubOrganizations({ items: [], totalCount: 0 });
    setView(view === ProviderHubViewEnum.ProviderClaimsInsights ? ProviderHubViewEnum.ProviderClaimsInsights : ProviderHubViewEnum.EmptyState);
    setFiltersChangedSinceLastSearch(false);
    setActiveServiceAreaStatusFilters(new Set());
    setAppliedProviderFilters(null);
  }

  const handleSearchClick = async () => {
    setPreDrillInFiltersSnapshot(null);
    await refreshData(true);
    if (filterState.viewByOption === ProviderViewByEnum.Hospital) {
      setView(ProviderHubViewEnum.Hospital);
      setShowFilterToggle(JSON.stringify(filterState) !== JSON.stringify({ ...initialFilterState, viewByOption: ProviderViewByEnum.Hospital }))
    } else if (filterState.viewByOption === ProviderViewByEnum.Organization) {
      setView(ProviderHubViewEnum.Organization);
      setShowFilterToggle(JSON.stringify(filterState) !== JSON.stringify({ ...initialFilterState, viewByOption: ProviderViewByEnum.Organization }) && filterState.viewByOption === ProviderViewByEnum.Organization)
    } else if (filterState.searchByOption !== ProviderSearchByEnum.ContractEntity) {
      setView(view === ProviderHubViewEnum.ProviderClaimsInsights ? ProviderHubViewEnum.ProviderClaimsInsights : ProviderHubViewEnum.Providers);
      setShowFilterToggle(false);
    } else {
      setView(ProviderHubViewEnum.ContractEntity);
      setShowFilterToggle(JSON.stringify(filterState) !== JSON.stringify({ ...initialFilterState, searchByOption: ProviderSearchByEnum.ContractEntity, viewByOption: ProviderViewByEnum.ContractEntity }) && filterState.searchByOption === ProviderSearchByEnum.ContractEntity)
    }
  }

  const rangeOrNull = (min: number, max: number, defMin: number, defMax: number): { min: number | null; max: number | null } =>
    (min === defMin && max === defMax) ? { max: null, min: null } : { max, min };

  /** Builds a filters request model for Providers from the current filter state. */
  const buildProviderHubProvidersFiltersRequestModel = (): ProviderHubFiltersRequestModel => {
    // If the user has not explicitly checked any counties, the active status
    // pills implicitly constrain the search to the matching counties so the
    // backend doesn't return Opportunity (or any non-selected status) results.
    const effectiveRegionSetIds = filterState.regionSetIds.length > 0
      ? filterState.regionSetIds.map(Number)
      : Array.from(activeServiceAreaStatusFilters).flatMap(s => Array.from(statusToRegionSet.get(s) ?? []));

    /* oxlint-disable sort-keys */
    return {
      projectId: projectContext?.project?.projectId ?? null,
      projectRunConfigurationId: filterState.membershipType === MembershipType.Primary ? filterState.projectRunConfigurationId : filterState.companionProjectRunConfigurationId,
      searchByOption: filterState.searchByOption,
      searchByInput: filterState.searchByInput,
      regionSetIds: effectiveRegionSetIds,
      specialtyGroupCodes: filterState.specialtyGroupCodes.map(item => item.key),
      providerNetworkTypes: filterState.providerNetworkTypes,
      providerActionStatuses: filterState.providerActionStatuses,
      closeGapsMin: filterState.closeGapsMin,
      closeGapsMax: filterState.closeGapsMax,
      accessPointCountMin: filterState.accessPointCountMin,
      accessPointCountMax: filterState.accessPointCountMax,
      effectiveness: filterState.effectiveness,
      efficiency: filterState.efficiency,
      motiveScore: filterState.motiveScore,
      careJourneyCostScore: filterState.careJourneyCostScore,
      careJourneyQualityScore: filterState.careJourneyQualityScore,
      totalClaimsVolume: filterState.totalClaimsVolume.map(item => item.key),
      totalNewPatientIndicator: filterState.totalNewPatientIndicator.map(item => item.key),
      commercialClaimsVolume: filterState.commercialClaimsVolume.map(item => item.key),
      commercialNewPatientIndicator: filterState.commercialNewPatientIndicator.map(item => item.key),
      medicaidClaimsVolume: filterState.medicaidClaimsVolume.map(item => item.key),
      medicaidNewPatientIndicator: filterState.medicaidNewPatientIndicator.map(item => item.key),
      medicareClaimsVolume: filterState.medicareClaimsVolume.map(item => item.key),
      medicareNewPatientIndicator: filterState.medicareNewPatientIndicator.map(item => item.key),
      tricareClaimsVolume: filterState.tricareClaimsVolume.map(item => item.key),
      tricareNewPatientIndicator: filterState.tricareNewPatientIndicator.map(item => item.key),
      vaClaimsVolume: filterState.vaClaimsVolume.map(item => item.key),
      vaNewPatientIndicator: filterState.vaNewPatientIndicator.map(item => item.key),
      topProcedureCodes: filterState.topProcedures.map(item => item.key),
      topConditionCodes: filterState.topConditions.map(item => item.key),
      topPrescriptions: filterState.topPrescriptions.map(item => item.key),
      locationConfidence: filterState.locationConfidence.map(item => item.key),
      networkIntegrityProjectOptionsExclusions: filterState.networkIntegrityProjectOptionsExclusions,
      networkIntegrityCriticalAlerts: filterState.networkIntegrityCriticalAlerts,
      networkIntegrityAttestationBasedAlerts: filterState.networkIntegrityAttestationBasedAlerts,
      networkIntegrityExcessAddressesMin: filterState.networkIntegrityExcessAddressesMin,
      networkIntegrityExcessAddressesMax: filterState.networkIntegrityExcessAddressesMax,
      networkIntegrityExcessSpecialtiesMin: filterState.networkIntegrityExcessSpecialtiesMin,
      networkIntegrityExcessSpecialtiesMax: filterState.networkIntegrityExcessSpecialtiesMax,
      pageSize: defaultPageSize,
      sortColumnAndOrder: filterState.sortColumnAndOrder
    };
    /* oxlint-enable sort-keys */
  };

  const refreshData = async (useFirstPage: boolean = false) => {
    if (useFirstPage) {
      setPageIndex(1);
      setResetToFirstPage(true);
    }

    // If the user has not explicitly checked any counties, the active status
    // pills implicitly constrain the search to the matching counties so the
    // backend doesn't return Opportunity (or any non-selected status) results.
    const effectiveRegionSetIds = filterState.regionSetIds.length > 0
      ? filterState.regionSetIds.map(Number)
      : Array.from(activeServiceAreaStatusFilters).flatMap(s => Array.from(statusToRegionSet.get(s) ?? []));

    setLoading(true);
    // Any branch below can throw; the spinner must still clear, so the whole dispatch is wrapped.
    try {
      if (filterState.viewByOption === ProviderViewByEnum.Hospital) {
        /* oxlint-disable sort-keys */
        const filterRequestModel: ProviderHubHospitalsFiltersRequestModel = {
          projectId: projectContext?.project?.projectId ?? null,
          projectRunConfigurationId: filterState.membershipType === MembershipType.Primary ? filterState.projectRunConfigurationId : filterState.companionProjectRunConfigurationId,
          searchByOption: filterState.searchByOption,
          searchByInput: filterState.searchByInput,
          regionSetIds: effectiveRegionSetIds,
          specialtyGroupCodes: filterState.specialtyGroupCodes.map(item => item.key),
          providerNetworkTypes: filterState.providerNetworkTypes,
          providerActionStatuses: filterState.providerActionStatuses,
          closeGapsMin: filterState.closeGapsMin,
          closeGapsMax: filterState.closeGapsMax,
          effectiveness: [],
          efficiency: [],
          motiveScore: [],
          careJourneyCostScore: [],
          careJourneyQualityScore: [],
          effectivenessMin: rangeOrNull(filterState.effectivenessMin, filterState.effectivenessMax, defaultEffectivenessRangeMin, defaultEffectivenessRangeMax).min,
          effectivenessMax: rangeOrNull(filterState.effectivenessMin, filterState.effectivenessMax, defaultEffectivenessRangeMin, defaultEffectivenessRangeMax).max,
          efficiencyMin: rangeOrNull(filterState.efficiencyMin, filterState.efficiencyMax, defaultEfficiencyRangeMin, defaultEfficiencyRangeMax).min,
          efficiencyMax: rangeOrNull(filterState.efficiencyMin, filterState.efficiencyMax, defaultEfficiencyRangeMin, defaultEfficiencyRangeMax).max,
          motiveScoreMin: rangeOrNull(filterState.motiveScoreMin, filterState.motiveScoreMax, defaultMotiveScoreRangeMin, defaultMotiveScoreRangeMax).min,
          motiveScoreMax: rangeOrNull(filterState.motiveScoreMin, filterState.motiveScoreMax, defaultMotiveScoreRangeMin, defaultMotiveScoreRangeMax).max,
          careJourneyCostScoreMin: rangeOrNull(filterState.careJourneyCostScoreMin, filterState.careJourneyCostScoreMax, defaultCareJourneyCostRangeMin, defaultCareJourneyCostRangeMax).min,
          careJourneyCostScoreMax: rangeOrNull(filterState.careJourneyCostScoreMin, filterState.careJourneyCostScoreMax, defaultCareJourneyCostRangeMin, defaultCareJourneyCostRangeMax).max,
          careJourneyQualityScoreMin: rangeOrNull(filterState.careJourneyQualityScoreMin, filterState.careJourneyQualityScoreMax, defaultCareJourneyQualityRangeMin, defaultCareJourneyQualityRangeMax).min,
          careJourneyQualityScoreMax: rangeOrNull(filterState.careJourneyQualityScoreMin, filterState.careJourneyQualityScoreMax, defaultCareJourneyQualityRangeMin, defaultCareJourneyQualityRangeMax).max,
          includeUnknownEffectiveness: filterState.includeUnknownEffectiveness,
          includeUnknownEfficiency: filterState.includeUnknownEfficiency,
          includeUnknownMotiveScore: filterState.includeUnknownMotiveScore,
          includeUnknownCareJourneyCostScore: filterState.includeUnknownCareJourneyCostScore,
          includeUnknownCareJourneyQualityScore: filterState.includeUnknownCareJourneyQualityScore,
          servicingProviderCountMinimum: filterState.servicingProviderCountMin,
          servicingProviderCountMaximum: filterState.servicingProviderCountMax,
          sortHospitalColumnAndOrder: filterState.sortHospitalColumnAndOrder,
          pageSize: defaultPageSize,
        };
        /* oxlint-enable sort-keys */
        setAppliedProviderFilters(null);

        const refreshedData = await providerHubService.getProviderHubHospitals(useFirstPage ? 1 : pageIndex, pageSize, filterRequestModel);
        setProviderHubHospitals(refreshedData);
        setProviderHubServicingProviders({ items: [], totalCount: 0 });
        setProviderHubContractEntities({ items: [], totalCount: 0 });
        setProviderHubOrganizations({ items: [], totalCount: 0 });
      } else if (filterState.viewByOption === ProviderViewByEnum.Organization) {
        /* oxlint-disable sort-keys */
        const filterRequestModel: ProviderHubOrganizationsFiltersRequestModel = {
          projectId: projectContext?.project?.projectId ?? null,
          projectRunConfigurationId: filterState.membershipType === MembershipType.Primary ? filterState.projectRunConfigurationId : filterState.companionProjectRunConfigurationId,
          searchByOption: filterState.searchByOption,
          searchByInput: filterState.searchByInput,
          regionSetIds: effectiveRegionSetIds,
          specialtyGroupCodes: filterState.specialtyGroupCodes.map(item => item.key),
          providerNetworkTypes: filterState.providerNetworkTypes,
          providerActionStatuses: filterState.providerActionStatuses,
          closeGapsMin: filterState.closeGapsMin,
          closeGapsMax: filterState.closeGapsMax,
          effectiveness: [],
          efficiency: [],
          motiveScore: [],
          careJourneyCostScore: [],
          careJourneyQualityScore: [],
          effectivenessMin: rangeOrNull(filterState.effectivenessMin, filterState.effectivenessMax, defaultEffectivenessRangeMin, defaultEffectivenessRangeMax).min,
          effectivenessMax: rangeOrNull(filterState.effectivenessMin, filterState.effectivenessMax, defaultEffectivenessRangeMin, defaultEffectivenessRangeMax).max,
          efficiencyMin: rangeOrNull(filterState.efficiencyMin, filterState.efficiencyMax, defaultEfficiencyRangeMin, defaultEfficiencyRangeMax).min,
          efficiencyMax: rangeOrNull(filterState.efficiencyMin, filterState.efficiencyMax, defaultEfficiencyRangeMin, defaultEfficiencyRangeMax).max,
          motiveScoreMin: rangeOrNull(filterState.motiveScoreMin, filterState.motiveScoreMax, defaultMotiveScoreRangeMin, defaultMotiveScoreRangeMax).min,
          motiveScoreMax: rangeOrNull(filterState.motiveScoreMin, filterState.motiveScoreMax, defaultMotiveScoreRangeMin, defaultMotiveScoreRangeMax).max,
          careJourneyCostScoreMin: rangeOrNull(filterState.careJourneyCostScoreMin, filterState.careJourneyCostScoreMax, defaultCareJourneyCostRangeMin, defaultCareJourneyCostRangeMax).min,
          careJourneyCostScoreMax: rangeOrNull(filterState.careJourneyCostScoreMin, filterState.careJourneyCostScoreMax, defaultCareJourneyCostRangeMin, defaultCareJourneyCostRangeMax).max,
          careJourneyQualityScoreMin: rangeOrNull(filterState.careJourneyQualityScoreMin, filterState.careJourneyQualityScoreMax, defaultCareJourneyQualityRangeMin, defaultCareJourneyQualityRangeMax).min,
          careJourneyQualityScoreMax: rangeOrNull(filterState.careJourneyQualityScoreMin, filterState.careJourneyQualityScoreMax, defaultCareJourneyQualityRangeMin, defaultCareJourneyQualityRangeMax).max,
          includeUnknownEffectiveness: filterState.includeUnknownEffectiveness,
          includeUnknownEfficiency: filterState.includeUnknownEfficiency,
          includeUnknownMotiveScore: filterState.includeUnknownMotiveScore,
          includeUnknownCareJourneyCostScore: filterState.includeUnknownCareJourneyCostScore,
          includeUnknownCareJourneyQualityScore: filterState.includeUnknownCareJourneyQualityScore,
          servicingProviderCountMinimum: filterState.servicingProviderCountMin,
          servicingProviderCountMaximum: filterState.servicingProviderCountMax,
          sortContractEntityColumnAndOrder: filterState.sortContractEntityColumnAndOrder,
          sortOrganizationColumnAndOrder: filterState.sortOrganizationColumnAndOrder,
          pageSize: defaultPageSize,
        };
        /* oxlint-enable sort-keys */
        setAppliedProviderFilters(null);

        const refreshedData = await providerHubService.getProviderHubOrganizations(useFirstPage ? 1 : pageIndex, pageSize, filterRequestModel);
        setProviderHubOrganizations(refreshedData);
        setProviderHubServicingProviders({ items: [], totalCount: 0 });
        setProviderHubContractEntities({ items: [], totalCount: 0 });
      } else if (filterState.searchByOption !== ProviderSearchByEnum.ContractEntity) {
        const filterRequestModel = buildProviderHubProvidersFiltersRequestModel();
        setAppliedProviderFilters(filterRequestModel);

        const refreshedData = await providerHubService.getProviderHubServicingProviders(useFirstPage ? 1 : pageIndex, pageSize, filterRequestModel);
        setProviderHubServicingProviders(refreshedData);
        setProviderHubContractEntities({ items: [], totalCount: 0 });
        setProviderHubOrganizations({ items: [], totalCount: 0 });
      } else {
        /* oxlint-disable sort-keys */
        const filterRequestModel: ProviderHubContractEntityFiltersRequestModel = {
          projectId: projectContext?.project?.projectId ?? null,
          projectRunConfigurationId: filterState.membershipType === MembershipType.Primary ? filterState.projectRunConfigurationId : filterState.companionProjectRunConfigurationId,
          searchByOption: filterState.searchByOption,
          searchByInput: filterState.searchByInput,
          regionSetIds: effectiveRegionSetIds,
          specialtyGroupCodes: filterState.specialtyGroupCodes.map(item => item.key),
          providerNetworkTypes: filterState.providerNetworkTypes,
          providerActionStatuses: filterState.providerActionStatuses,
          closeGapsMin: filterState.closeGapsMin,
          closeGapsMax: filterState.closeGapsMax,
          effectiveness: [],
          efficiency: [],
          motiveScore: [],
          effectivenessMin: rangeOrNull(filterState.effectivenessMin, filterState.effectivenessMax, defaultEffectivenessRangeMin, defaultEffectivenessRangeMax).min,
          effectivenessMax: rangeOrNull(filterState.effectivenessMin, filterState.effectivenessMax, defaultEffectivenessRangeMin, defaultEffectivenessRangeMax).max,
          efficiencyMin: rangeOrNull(filterState.efficiencyMin, filterState.efficiencyMax, defaultEfficiencyRangeMin, defaultEfficiencyRangeMax).min,
          efficiencyMax: rangeOrNull(filterState.efficiencyMin, filterState.efficiencyMax, defaultEfficiencyRangeMin, defaultEfficiencyRangeMax).max,
          motiveScoreMin: rangeOrNull(filterState.motiveScoreMin, filterState.motiveScoreMax, defaultMotiveScoreRangeMin, defaultMotiveScoreRangeMax).min,
          motiveScoreMax: rangeOrNull(filterState.motiveScoreMin, filterState.motiveScoreMax, defaultMotiveScoreRangeMin, defaultMotiveScoreRangeMax).max,
          includeUnknownEffectiveness: filterState.includeUnknownEffectiveness,
          includeUnknownEfficiency: filterState.includeUnknownEfficiency,
          includeUnknownMotiveScore: filterState.includeUnknownMotiveScore,
          includeUnknownCareJourneyCostScore: filterState.includeUnknownCareJourneyCostScore,
          includeUnknownCareJourneyQualityScore: filterState.includeUnknownCareJourneyQualityScore,
          pageSize: defaultPageSize,
          sortContractEntityColumnAndOrder: filterState.sortContractEntityColumnAndOrder,
          careJourneyCostScore: [],
          careJourneyQualityScore: [],
          careJourneyCostScoreMin: rangeOrNull(filterState.careJourneyCostScoreMin, filterState.careJourneyCostScoreMax, defaultCareJourneyCostRangeMin, defaultCareJourneyCostRangeMax).min,
          careJourneyCostScoreMax: rangeOrNull(filterState.careJourneyCostScoreMin, filterState.careJourneyCostScoreMax, defaultCareJourneyCostRangeMin, defaultCareJourneyCostRangeMax).max,
          careJourneyQualityScoreMin: rangeOrNull(filterState.careJourneyQualityScoreMin, filterState.careJourneyQualityScoreMax, defaultCareJourneyQualityRangeMin, defaultCareJourneyQualityRangeMax).min,
          careJourneyQualityScoreMax: rangeOrNull(filterState.careJourneyQualityScoreMin, filterState.careJourneyQualityScoreMax, defaultCareJourneyQualityRangeMin, defaultCareJourneyQualityRangeMax).max,
          servicingProviderCountMinimum: filterState.servicingProviderCountMin,
          servicingProviderCountMaximum: filterState.servicingProviderCountMax,
          networkIntegrityProjectOptionsExclusions: filterState.networkIntegrityProjectOptionsExclusions,
          networkIntegrityCriticalAlerts: filterState.networkIntegrityCriticalAlerts,
          networkIntegrityAttestationBasedAlerts: filterState.networkIntegrityAttestationBasedAlerts,
          networkIntegrityExcessAddressesMin: filterState.networkIntegrityExcessAddressesMin,
          networkIntegrityExcessAddressesMax: filterState.networkIntegrityExcessAddressesMax,
          networkIntegrityExcessSpecialtiesMin: filterState.networkIntegrityExcessSpecialtiesMin,
          networkIntegrityExcessSpecialtiesMax: filterState.networkIntegrityExcessSpecialtiesMax
        };
        /* oxlint-enable sort-keys */
        setAppliedProviderFilters(null);

        const refreshedData = await providerHubService.getProviderHubContractEntities(useFirstPage ? 1 : pageIndex, pageSize, filterRequestModel);
        setProviderHubContractEntities(refreshedData);
        setProviderHubServicingProviders({ items: [], totalCount: 0 });
        setProviderHubOrganizations({ items: [], totalCount: 0 });
      }
    } finally {
      // Changes to error-handling here (catch or finally blocks) should also be reflected in applyServicingProviderFilters
      setLoading(false);
      setFiltersChangedSinceLastSearch(false);
    }
  }

  /** Sets the filter to the given value and sets `filtersChangedSinceLastSearch`. */
  const setFilter = <T extends keyof ProviderHubFilters>(key: T, value: ProviderHubFilters[T]) => {
    filterStateDispatch({ key, type: 'SET_FILTER', value });
    setFiltersChangedSinceLastSearch(true);
  };

  /**
   * Sets the filter to the given value but skips setting `filtersChangedSinceLastSearch`.
   * Should ONLY be used if the result set would still be the same with this change, or if a new search is programmatically kicked off.
   * Generally this should only be used for programmatic/dynamic filter updates that you don't want to show the
   * "Update Results" search button text for.
   */
  const setFilterWithoutMarkingAsChanged = <T extends keyof ProviderHubFilters>(key: T, value: ProviderHubFilters[T]) => {
    filterStateDispatch({ key, type: 'SET_FILTER', value });
  };

  /**
   * Used by PCI Dashboard's Network Expansion Opportunity drill-ins to update filters to
   * out-of-network providers, efficiency and effectiveness scores of 4-5, and optionally a specialty group.
   * Then switch to the Providers list view, and refresh provider data with those filters.
   * @param specialtyGroupCode Optional specialty group to filter to, otherwise specialties
   * are left unfiltered (all returned).
   */
  const viewNetworkExpansionOpportunityProviders = async (specialtyGroupCode?: string) =>
    viewProvidersFilteredFromReadyForReview([...outOfNetworkProviderNetworkTypes], [4, 5], specialtyGroupCode);

  /**
   * Used by PCI Dashboard's Network Expansion Opportunity drill-ins to update filters to
   * network providers, efficiency and effectiveness scores of 1-2, and optionally a specialty group.
   * Then switch to the Providers list view, and refresh provider data with those filters.
   * @param specialtyGroupCode Optional specialty group to filter to, otherwise specialties
   * are left unfiltered (all returned).
   */
  const viewUnderperformingProviders = async (specialtyGroupCode?: string) =>
    viewProvidersFilteredFromReadyForReview([ProviderNetworkTypeEnum.Network], [1, 2], specialtyGroupCode);

  /**
   * Used by PCI Dashboard's Ready for Review drill-ins to update filters to set specific
   * provider sources, efficiency and effectiveness scores and optionally a specialty group.
   * Then switch to the Providers list view, and refresh provider data with those filters.
   * @param providerNetworkTypes A set of Provider Sources/Network Types to update filters to.
   * @param effectivenessEfficiencyScores A set of integer scores 0-5 (where 0 represents "Unknown")
   * to filter both Effectiveness and Efficiency to.
   * @param specialtyGroupCode Optional specialty group to filter to, otherwise specialties
   * are left unfiltered (all returned).
   */
  const viewProvidersFilteredFromReadyForReview = (providerNetworkTypes: ProviderNetworkTypeEnum[], effectivenessEfficiencyScores: (number | null)[], specialtyGroupCode?: string) => {
    // API expects score of "Unknown" to be passed as null, not 0.
    const scores = effectivenessEfficiencyScores.map((s) => s === 0 ? null : s);

    const filterUpdates: Partial<ProviderHubFilters> = {
      effectiveness: scores,
      efficiency: scores,
      providerNetworkTypes,
    };
    const filtersRequestModelOverrides: Partial<ProviderHubFiltersRequestModel> = {
      effectiveness: scores,
      efficiency: scores,
      providerNetworkTypes,
    };

    const specialtyGroupOption = specialtyGroupCode ? specialtyOptions.find(option => option.key === specialtyGroupCode) : undefined;
    if (specialtyGroupOption) {
      filterUpdates.specialtyGroupCodes = [specialtyGroupOption];
      filtersRequestModelOverrides.specialtyGroupCodes = [specialtyGroupOption.key];
    }

    return applyPciDashboardDrillInFilters(filterUpdates, filtersRequestModelOverrides);
  };

  /**
   * Used by PCI Dashboard's drill-ins to update filters to set a specific LOB's claims volume label,
   * switch to the Providers list view, and refresh provider data with those filters.
   * @param filterFieldKey An LOB's claims volume filter key.
   * @param claimsVolumeLabel The claims volume label filter to add.
   */
  const viewProvidersFilteredByClaimsVolume = (filterFieldKey: ClaimsVolumeFilterFieldKey, claimsVolumeLabel: ClaimsActivityVolumeLabelEnum) => {
    return applyPciDashboardDrillInFilters(
      { [filterFieldKey]: [{ key: claimsVolumeLabel, name: claimsVolumeLabel }] },
      { [filterFieldKey]: [claimsVolumeLabel] }
    );
  };

  /**
   * Used by PCI Dashboard's drill-ins to update filters to set a specific effectiveness or efficiency score,
   * switch to the Providers list view, and refresh provider data with those filters.
   * @param filterFieldKey Either `"effectiveness"` or `"efficiency"`.
   * @param score An integer 0-5, 0 representing score of "Unknown".
   */
  const viewProvidersFilteredByScore = (filterFieldKey: "effectiveness" | "efficiency", score: number | null) => {
    // API expects score of "Unknown" to be passed as null, not 0.
    const normalizedScore = score === 0 ? null : score;
    return applyPciDashboardDrillInFilters(
      { [filterFieldKey]: [normalizedScore] },
      { [filterFieldKey]: [normalizedScore] }
    );
  };

  /**
   * Shared shell for PCI Dashboard's drill-ins: snapshots the pre-drill-in values of whichever `filterState`
   * keys are about to change (to store in `preDrillInFiltersSnapshot` to later revert drill-in), preserves the
   * pre-click dirty-filters state, applies `filterUpdates` to `filterState`, switches to the Providers list
   * view, and refreshes provider data using `appliedProviderFilters` with `filtersRequestModelOverrides`.
   * @param filterUpdates `filterState` fields to set for this drill-in.
   * @param filtersRequestModelOverrides The corresponding overrides to merge into the applied filters request model.
   */
  const applyPciDashboardDrillInFilters = async (filterUpdates: Partial<ProviderHubFilters>, filtersRequestModelOverrides: Partial<ProviderHubFiltersRequestModel>) => {
    const filtersWereDirtyBeforeClick = filtersChangedSinceLastSearch;

    // Save pre-drill-in state so the drill-in can be reverted
    setPreDrillInFiltersSnapshot({
      appliedProviderFilters,
      filterOverrides: Object.fromEntries(
        Object.keys(filterUpdates).map(key => [key, filterState[key as keyof ProviderHubFilters]])
      ) as Partial<ProviderHubFilters>,
    });

    // Update filterState to include drill-in's filters. Don't mark filters as changed to avoid flashing "Update Results" button text
    Object.entries(filterUpdates).forEach(([key, value]) => setFilterWithoutMarkingAsChanged(key as keyof ProviderHubFilters, value));

    const filtersRequestModel: ProviderHubFiltersRequestModel = {
      // Use previously applied filters; do not auto-apply dirty filters
      ...appliedProviderFilters,
      ...filtersRequestModelOverrides,
    };

    navigateToProvidersFromPciDashboardView();

    try {
      await applyServicingProviderFilters(filtersRequestModel);
    } finally {
      // Drill-in only uses appliedProviderFilters, so no dirty filters on click are applied.
      // So if filters were dirty before the drill-in, they are still dirty now.
      setFiltersChangedSinceLastSearch(filtersWereDirtyBeforeClick);
    }

  };

  /**
   * Rolls back `filterState` and `appliedProviderFilters` using the `snapshot`, then reloads the servicing provider results.
   * Resets `preDrillInFiltersSnapshot` but keeps the `filtersChangedSinceLastSearch` flag as it was before the revert.
   */
  const revertPciDashboardDrillInFilters = async (snapshot: PreDrillInFiltersSnapshot) => {
    const wasDirtyBeforeRevert = filtersChangedSinceLastSearch;
    // Restore filters overridden by drill-in. Don't mark filters as changed to avoid flashing "Update Results" button text
    Object.entries(snapshot.filterOverrides).forEach(([key, value]) => setFilterWithoutMarkingAsChanged(key as keyof ProviderHubFilters, value));
    try {
      await applyServicingProviderFilters(snapshot.appliedProviderFilters);
    } finally {
      setPreDrillInFiltersSnapshot(null);
      setFiltersChangedSinceLastSearch(wasDirtyBeforeRevert);
    }
  };

  /**
   * Mimics refreshData with `useFirstPage = true` but using a passed filters model instead of the state.
   * @param filtersRequestModel An API call ready filters model created from `buildProviderHubProvidersFiltersRequestModel()`.
   */
  const applyServicingProviderFilters = async (filtersRequestModel: ProviderHubFiltersRequestModel) => {
    setLoading(true);
    setPageIndex(1);
    setResetToFirstPage(true);

    // PCI Dashboard data should stay in sync with filters regardless of if getProviderHubServicingProviders errors or not
    setAppliedProviderFilters(filtersRequestModel);

    try {
      const refreshedData = await providerHubService.getProviderHubServicingProviders(1, pageSize, filtersRequestModel);
      setProviderHubServicingProviders(refreshedData);
      setProviderHubContractEntities({ items: [], totalCount: 0 });
      setProviderHubOrganizations({ items: [], totalCount: 0 });
    } finally {
      // finally should match refreshData
      setLoading(false);
      setFiltersChangedSinceLastSearch(false);
    }
  }

  /** Sets view to Providers list and removes PCI Dashboard segment from the URL. */
  const navigateToProvidersFromPciDashboardView = () => {
    setView(ProviderHubViewEnum.Providers);

    // Update the url to drop the "/provider-claims-insights" pseudo-tab suffix
    if (history.location.pathname.endsWith(PCI_DASHBOARD_SUBROUTE)) {
      const targetPathname = history.location.pathname.slice(0, -PCI_DASHBOARD_SUBROUTE.length);
      history.push(targetPathname + history.location.search);
    }

    window.scrollTo(0, 0);
  }

  const serviceAreaSelectionText = useMemo(() => {
    if (filterState.regionSetIds.length === 0) {
      return allOptionsText;
    }
    return getSelectionText(filterState.regionSetIds, serviceAreaOptions?.flatMap(sa => sa.children));
  }, [filterState.regionSetIds, serviceAreaOptions]);

  const serviceAreaStatusFilters = useMemo<{ key: string; displayValue: string }[]>(() => {
    return SERVICE_AREA_STATUS_DISPLAY_ORDER
      .filter(s => (statusToRegionSet.get(s)?.size ?? 0) > 0)
      .map(s => ({ displayValue: s, key: s }));
  }, [statusToRegionSet]);

  const serviceAreaMatchFilter = useCallback(
    (child: DropdownItem<string>, activeKeys: string[]) => {
      const childRegionSetId = Number(child.key);
      return activeKeys.some(key => statusToRegionSet.get(key as RegionSetStatusType)?.has(childRegionSetId) ?? false);
    },
    [statusToRegionSet]
  );

  const specialtySelectionText = useMemo(() => {
    if (filterState.specialtyGroupCodes.length === 0) {
      return allOptionsText;
    }
    return getSelectionText(filterState.specialtyGroupCodes, specialtyOptions);
  }, [filterState.specialtyGroupCodes, specialtyOptions]);

  const showAdvancedFiltersIndicator = useMemo<boolean>(() => {
    if (filterState.providerActionStatuses.length > 0) return true;
    if (filterState.providerNetworkTypes.length > 0) return true;
    if (filterState.totalClaimsVolume.length > 0) return true;
    if (filterState.totalNewPatientIndicator.length > 0) return true;
    if (filterState.commercialClaimsVolume.length > 0) return true;
    if (filterState.commercialNewPatientIndicator.length > 0) return true;
    if (filterState.medicaidClaimsVolume.length > 0) return true;
    if (filterState.medicaidNewPatientIndicator.length > 0) return true;
    if (filterState.medicareClaimsVolume.length > 0) return true;
    if (filterState.medicareNewPatientIndicator.length > 0) return true;
    if (filterState.tricareClaimsVolume.length > 0) return true;
    if (filterState.tricareNewPatientIndicator.length > 0) return true;
    if (filterState.vaClaimsVolume.length > 0) return true;
    if (filterState.vaNewPatientIndicator.length > 0) return true;
    if (filterState.topProcedures.length > 0) return true;
    if (filterState.topConditions.length > 0) return true;
    if (filterState.topPrescriptions.length > 0) return true;
    if (filterState.motiveScore.length > 0) return true;
    if (filterState.locationConfidence.length > 0) return true;
    if (filterState.closeGapsMin !== defaultCloseGapsRangeMin) return true;
    if (filterState.closeGapsMax !== defaultCloseGapsRangeMax) return true;
    if (filterState.accessPointCountMin !== defaultAccessPointsRangeMin) return true;
    if (filterState.accessPointCountMax !== defaultAccessPointsRangeMax) return true;
    if (filterState.effectiveness.length > 0) return true;
    if (filterState.efficiency.length > 0) return true;
    if (filterState.effectivenessMin !== defaultEffectivenessRangeMin) return true;
    if (filterState.effectivenessMax !== defaultEffectivenessRangeMax) return true;
    if (filterState.efficiencyMin !== defaultEfficiencyRangeMin) return true;
    if (filterState.efficiencyMax !== defaultEfficiencyRangeMax) return true;
    if (filterState.careJourneyCostScore?.length > 0) return true;
    if (filterState.careJourneyQualityScore?.length > 0) return true;
    if (filterState.careJourneyCostScoreMin !== defaultCareJourneyCostRangeMin) return true;
    if (filterState.careJourneyCostScoreMax !== defaultCareJourneyCostRangeMax) return true;
    if (filterState.careJourneyQualityScoreMin !== defaultCareJourneyQualityRangeMin) return true;
    if (filterState.careJourneyQualityScoreMax !== defaultCareJourneyQualityRangeMax) return true;
    if (filterState.motiveScoreMin !== defaultMotiveScoreRangeMin) return true;
    if (filterState.motiveScoreMax !== defaultMotiveScoreRangeMax) return true;
    if (filterState.includeUnknownEffectiveness === false) return true;
    if (filterState.includeUnknownEfficiency === false) return true;
    if (filterState.includeUnknownMotiveScore === false) return true;
    if (filterState.includeUnknownCareJourneyCostScore === false) return true;
    if (filterState.includeUnknownCareJourneyQualityScore === false) return true;
    if (filterState.servicingProviderCountMin !== defaultServicingProviderCountMin) return true;
    if (filterState.servicingProviderCountMax !== defaultServicingProviderCountMax) return true;
    if (filterState.networkIntegrityProjectOptionsExclusions?.length > 0) return true;
    if (filterState.networkIntegrityCriticalAlerts?.length > 0) return true;
    if (filterState.networkIntegrityAttestationBasedAlerts?.length > 0) return true;
    if (filterState.networkIntegrityExcessAddressesMin !== defaultExcessAddressesRangeMin) return true;
    if (filterState.networkIntegrityExcessAddressesMax !== defaultExcessAddressesRangeMax) return true;
    if (filterState.networkIntegrityExcessSpecialtiesMin !== defaultExcessSpecialtiesRangeMin) return true;
    if (filterState.networkIntegrityExcessSpecialtiesMax !== defaultExcessSpecialtiesRangeMax) return true;
    return false;
  }, [
    filterState.providerNetworkTypes,
    filterState.providerActionStatuses,
    filterState.closeGapsMin,
    filterState.closeGapsMax,
    filterState.accessPointCountMin,
    filterState.accessPointCountMax,
    filterState.efficiency,
    filterState.effectiveness,
    filterState.effectivenessMin,
    filterState.effectivenessMax,
    filterState.efficiencyMin,
    filterState.efficiencyMax,
    filterState.totalClaimsVolume,
    filterState.commercialClaimsVolume,
    filterState.medicaidClaimsVolume,
    filterState.medicareClaimsVolume,
    filterState.tricareClaimsVolume,
    filterState.vaClaimsVolume,
    filterState.totalNewPatientIndicator,
    filterState.commercialNewPatientIndicator,
    filterState.medicaidNewPatientIndicator,
    filterState.medicareNewPatientIndicator,
    filterState.tricareNewPatientIndicator,
    filterState.vaNewPatientIndicator,
    filterState.topProcedures,
    filterState.topConditions,
    filterState.topPrescriptions,
    filterState.motiveScore,
    filterState.locationConfidence,
    filterState.careJourneyCostScore,
    filterState.careJourneyQualityScore,
    filterState.careJourneyCostScoreMin,
    filterState.careJourneyCostScoreMax,
    filterState.careJourneyQualityScoreMin,
    filterState.careJourneyQualityScoreMax,
    filterState.motiveScoreMin,
    filterState.motiveScoreMax,
    filterState.includeUnknownEffectiveness,
    filterState.includeUnknownEfficiency,
    filterState.includeUnknownMotiveScore,
    filterState.includeUnknownCareJourneyCostScore,
    filterState.includeUnknownCareJourneyQualityScore,
    filterState.servicingProviderCountMax,
    filterState.servicingProviderCountMin,
    filterState.networkIntegrityProjectOptionsExclusions,
    filterState.networkIntegrityCriticalAlerts,
    filterState.networkIntegrityAttestationBasedAlerts,
    filterState.networkIntegrityExcessAddressesMin,
    filterState.networkIntegrityExcessAddressesMax,
    filterState.networkIntegrityExcessSpecialtiesMin,
    filterState.networkIntegrityExcessSpecialtiesMax
  ]);

  useEffect(() => {
    const loadServiceAreaCounties = async () => {
      if (!!filterState.projectId && !!filterState.projectRunConfigurationId) {
        const [counties, availableStatuses] = await Promise.all([
          projectService.getCountiesByRunConfigurationId(
            filterState.projectId.toString(),
            filterState.projectRunConfigurationId.toString(),
            true,
            true
          ),
          projectService
            .getAvailableStatuses(filterState.projectId, filterState.projectRunConfigurationId)
            .catch(() => [] as AvailableRegionStatusesModel[]),
        ]);
        if (counties && counties.length > 0) {
          const ssaToCanonicalRegionSetId = new Map<string, number>();
          counties.forEach(state => {
            state.children?.forEach(county => {
              ssaToCanonicalRegionSetId.set(county.countySSA, Number(county.key));
            });
          });

          const newStatusToRegionSet = new Map<RegionSetStatusType, Set<number>>();
          availableStatuses?.forEach(s => {
            if (s.regionCode != null && s.availableStatuses?.length > 0) {
              const canonicalRegionSetId = ssaToCanonicalRegionSetId.get(s.regionCode);
              if (canonicalRegionSetId != null) {
                const status = s.availableStatuses[0];
                if (!newStatusToRegionSet.has(status)) {
                  newStatusToRegionSet.set(status, new Set<number>());
                }
                newStatusToRegionSet.get(status).add(canonicalRegionSetId);
              }
            }
          });
          setStatusToRegionSet(newStatusToRegionSet);
          setActiveServiceAreaStatusFilters(prev =>
            new Set(Array.from(prev).filter(s => newStatusToRegionSet.has(s)))
          );
          const transformedCounties = counties.map(state => ({
            ...state,
            children: state.children
              ? state.children.sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name))
              : [],
            key: state.name,
            name: state.key,
          }))
            .sort((a, b) => a.name.localeCompare(b.name));
          setServiceAreaOptions(transformedCounties);
        } else {
          setServiceAreaLoadFailed(true);
        }
      }
    };

    loadServiceAreaCounties();
  }, [filterState.projectId, filterState.projectRunConfigurationId]);

  useEffect(() => {
    if (!projectContext?.runConfiguration) return;

    if (!!projectContext.runConfiguration.specialtyGroups) {
      const sortedSpecialtyGroups = projectContext.runConfiguration.specialtyGroups.sort((a, b) => a.name.localeCompare(b.name));
      const specialties = sortedSpecialtyGroups.map(item => ({
        key: item.code,
        name: formatSpecialtyWithCode(item),
      }));
      setSpecialtyOptions(specialties);
      setSpecialtyGroupsLoadFailed(false);
    } else {
      setSpecialtyGroupsLoadFailed(true);
    }
  }, [projectContext?.runConfiguration?.specialtyGroups]);

  useEffect(() => {
    if (!projectContext?.runConfiguration) return;
    setShowMembershipTypeDropdown(projectContext.runConfiguration.projectRunConfigurationType != ProjectRunConfigurationType.Standard);
  }, [projectContext?.runConfiguration?.projectRunConfigurationType]);

  useEffect(() => {
    if (!projectContext?.runConfiguration || view === ProviderHubViewEnum.EmptyState) return;
    refreshData();
  }, [pageIndex, pageSize, sortColumnAndOrder, sortContractEntityColumnAndOrder]);

  useEffect(() => {
    if (!projectContext?.runConfiguration) return;
    const locationConfidenceFromPci = projectContext.runConfiguration.showLocationConfidenceScores;
    setShowLocationConfidenceFilter(locationConfidenceFromPci);
    setShowAccessPointLocationConfidence(locationConfidenceFromPci || ((projectContext?.project?.showNetworkIntegrityCriticalAlerts ?? false)
      && featureFlagContext.enabledFeatureFlags.has(Features.ENABLE_NETWORK_INTEGRITY_IN_PROVIDER_HUB)));
    setShowMotiveScoreFilters(projectContext.runConfiguration.showMotiveAppropriatenessScore);
    setShowPciFilters(projectContext.runConfiguration.showPciData);
    setShowTriCareFilters(projectContext.runConfiguration.showTricareClaimsVolumeColumn);
    setShowCareJourneyFilters(projectContext.runConfiguration.showCareJourneyScores);
    setShowVaFilters(projectContext.runConfiguration.showVaClaimsVolumeColumn);
  }, [featureFlagContext.enabledFeatureFlags, projectContext?.runConfiguration, projectContext?.project]);

  useEffect(() => {
    const niInProviderHubEnabled = featureFlagContext.enabledFeatureFlags?.has(Features.ENABLE_NETWORK_INTEGRITY_IN_PROVIDER_HUB) ?? false;
    setShowNetworkIntegrityFilters((projectContext?.project?.showNetworkIntegrityDashboard ?? false) && niInProviderHubEnabled);
    setShowNetworkIntegrityCriticalAlertsFilter((projectContext?.project?.showNetworkIntegrityCriticalAlerts ?? false) && niInProviderHubEnabled);
    setShowNetworkIntegrityAttestationBasedAlertsFilter((projectContext?.project?.showNetworkIntegrityAttestationBasedAlerts ?? false) && niInProviderHubEnabled);
  }, [projectContext?.project, featureFlagContext.enabledFeatureFlags]);

  useEffect(() => {
    const updateDefaultRanges = async () => {
      const processingState = [ProjectRunStatusEnum.Created, ProjectRunStatusEnum.Running, ProjectRunStatusEnum.Queued];

      const currentServicingProviderIngestionStatus = projectContext?.project?.servicingProviderIngestionStatus;

      const isProcessing = processingState.includes(currentServicingProviderIngestionStatus);

      const isMissingData = currentServicingProviderIngestionStatus === ProjectRunStatusEnum.MissingData;

      // Treat as error if we can't load the project context, project or status
      const isError = !currentServicingProviderIngestionStatus || currentServicingProviderIngestionStatus === ProjectRunStatusEnum.Failed;

      setIsProviderHubProcessing(isProcessing);
      setIsProviderHubMissingData(isMissingData);
      setIsProviderHubError(isError);

      if (!!filterState.projectId && !!filterState.projectRunConfigurationId && projectContext?.runConfiguration && !isProcessing && !isError) {

        try {
          const [metrics, maxProviders] = await Promise.all([
            providerHubService.getMaxProviderMetric(filterState.projectId, filterState.projectRunConfigurationId),
            providerHubService.getMaxContractEntityMetricAsync(filterState.projectId, filterState.projectRunConfigurationId),
          ]);

          setDefaultServicingProviderCountMax(maxProviders.servicingProviderCount);
          setFilterWithoutMarkingAsChanged("servicingProviderCountMax", maxProviders.servicingProviderCount);

          const gapsClosedMax = Math.max(metrics.maxGapsClosed, maxProviders.maxGapsClosed);
          setDefaultCloseGapsRangeMax(gapsClosedMax);
          setFilterWithoutMarkingAsChanged("closeGapsMax", gapsClosedMax);

          if (metrics.maxAccessPointCount == 0)
            metrics.maxAccessPointCount = 1; // this indicates that there is no data. Set this to a max of 1 in this case.

          setDefaultAccessPointsRangeMax(metrics.maxAccessPointCount);
          setFilterWithoutMarkingAsChanged("accessPointCountMax", metrics.maxAccessPointCount);
        } catch (error) {
          setMaxProviderMetricLoadFailed(true);
        }
      }
    };

    updateDefaultRanges();
  }, [
    filterState.projectId,
    filterState.projectRunConfigurationId,
    projectContext?.project?.servicingProviderIngestionStatus,
    projectContext?.project?.scenarioModelingIngestionStatus,
    projectContext?.runConfiguration
  ]);

  const invalidatePciDashboardQueries = () => {
    queryClient.invalidateQueries({ queryKey: [READY_FOR_REVIEW_QUERY_KEY_PREFIX] });
    queryClient.invalidateQueries({ queryKey: [CLAIMS_VOLUME_BREAKDOWN_QUERY_KEY_PREFIX] });
    queryClient.invalidateQueries({ queryKey: [EFFECTIVENESS_EFFICIENCY_BREAKDOWN_QUERY_KEY_PREFIX] });
  };

  useEffect(() => {
    const processingState = [ProjectRunStatusEnum.Created, ProjectRunStatusEnum.Running, ProjectRunStatusEnum.Queued];

    if (processingState.includes(projectContext?.project?.scenarioModelingIngestionStatus)) {
      setIsScenarioModelingProcessing(true);
    }
    else if (isScenarioModelingProcessing && projectContext?.project?.scenarioModelingIngestionStatus === ProjectRunStatusEnum.Succeeded && projectContext?.runConfiguration) {
      refreshData();
      setIsScenarioModelingProcessing(false);

      // Invalidate cached queries; scenario modeling could have changed results
      invalidatePciDashboardQueries();

      if (view === ProviderHubViewEnum.ProviderProfile && !!selectedProvider) {
        providerHubService
          .getServicingProviderDetails(projectContext?.project?.projectId, selectedProvider.projectRunConfigurationId, selectedProvider.servicingProviderKey)
          .then((providerDetails) => {
            setSelectedProvider(providerDetails);
          });
      }

      if (!projectContext?.project?.isReadOnly) {
        toast("New data available. Data refresh in progress.", {
          containerId: "z-index-9999",
          type: 'info',
        });
      }
    }
  }, [projectContext?.project?.scenarioModelingIngestionStatus]);

  const isScenarioModelingError = useMemo(() => {
    return projectContext?.project?.scenarioModelingIngestionStatus === ProjectRunStatusEnum.Failed;
  }, [projectContext?.project?.scenarioModelingIngestionStatus]);

  const isProjectReadOnly = useMemo(() => {
    return projectContext?.project?.isReadOnly ?? true;
  }, [projectContext?.project?.isReadOnly]);

  const membershipAliases = useMemo(() => {
    const DEFAULT_PRIMARY_ALIAS = "Primary Membership";
    const DEFAULT_SECONDARY_ALIAS = "Secondary Membership";

    if (!projectContext?.runConfiguration || projectContext.runConfiguration.projectRunConfigurationType === ProjectRunConfigurationType.Standard) {
      return { primary: DEFAULT_PRIMARY_ALIAS, secondary: DEFAULT_SECONDARY_ALIAS };
    }

    const primaryConfig = projectContext.project?.projectRunConfigurations?.find(
      (config) => config.projectRunConfigurationId === filterState.projectRunConfigurationId
    );
    const secondaryConfig = projectContext.project?.projectRunConfigurations?.find(
      (config) => config.projectRunConfigurationId === filterState.companionProjectRunConfigurationId
    );

    const primaryAlias = primaryConfig?.memberSetAlias ?? DEFAULT_PRIMARY_ALIAS;
    const secondaryAlias = secondaryConfig?.memberSetAlias ?? DEFAULT_SECONDARY_ALIAS;

    return { primary: primaryAlias, secondary: secondaryAlias };
  }, [projectContext?.project?.projectRunConfigurations, projectContext?.runConfiguration?.projectRunConfigurationType, filterState.projectRunConfigurationId, filterState.companionProjectRunConfigurationId]);

  const retryIngestion = useCallback(async () => {
    if (!filterState.projectId || !filterState.projectRunConfigurationId || isRetryingIngestion) return;

    setIsRetryingIngestion(true);
    try {
      const projectRunConfigurationIds = [filterState.projectRunConfigurationId];
      if (filterState.companionProjectRunConfigurationId) {
        projectRunConfigurationIds.push(filterState.companionProjectRunConfigurationId);
      }
      await providerHubService.resetIngestion(filterState.projectId, projectRunConfigurationIds);
      toast("Ingestion retry has been successfully created.", {
        containerId: "z-index-9999",
        type: "success",
      });
      await projectContext.forceUpdateProject();
    } catch {
      toast("Ingestion retry was NOT successful.", {
        containerId: "z-index-9999",
        type: "warning",
      });
    } finally {
      setIsRetryingIngestion(false);
    }
  }, [filterState.projectId, filterState.projectRunConfigurationId, filterState.companionProjectRunConfigurationId, isRetryingIngestion]);

  useEffect(() => {
    const ffEnabled = featureFlagContext.enabledFeatureFlags.has(Features.NETWORK_INTEGRITY_MARKET_PROVIDER_RESULTS);
    const hasMarketProviders = projectContext?.runConfiguration?.marketProviderSetId != null;

    if (!ffEnabled || !hasMarketProviders || !filterState.projectId || !filterState.projectRunConfigurationId) {
      setIsNiMarketProviderProcessing(false);
      setIsNiMarketProviderFailed(false);
      return;
    }

    providerHubService.getMarketProviderJobStatus(
      filterState.projectId,
      filterState.projectRunConfigurationId
    ).then(result => {
      setIsNiMarketProviderProcessing(result.isProcessing);
      setIsNiMarketProviderFailed(result.isFailed);
    }).catch(() => {
      setIsNiMarketProviderProcessing(false);
      setIsNiMarketProviderFailed(false);
    })
  }, [featureFlagContext.enabledFeatureFlags, filterState.projectId, filterState.projectRunConfigurationId, projectContext?.runConfiguration?.marketProviderSetId])

  const contextValue = {
    accessPointsPage,
    accessPointsPageSize,
    activeServiceAreaStatusFilters,
    adequacyCriticalityLoading,
    adequacyProviderScore,
    allOptionsText,
    appliedProviderFilters,
    contractEntityOptions,
    defaultAccessPointsRangeMax,
    defaultAccessPointsRangeMin,
    defaultCareJourneyCostRangeMax,
    defaultCareJourneyCostRangeMin,
    defaultCareJourneyQualityRangeMax,
    defaultCareJourneyQualityRangeMin,
    defaultCloseGapsRangeMax,
    defaultCloseGapsRangeMin,
    defaultEffectivenessRangeMax,
    defaultEffectivenessRangeMin,
    defaultEfficiencyRangeMax,
    defaultEfficiencyRangeMin,
    defaultExcessAddressesRangeMax,
    defaultExcessAddressesRangeMin,
    defaultExcessSpecialtiesRangeMax,
    defaultExcessSpecialtiesRangeMin,
    defaultMotiveScoreRangeMax,
    defaultMotiveScoreRangeMin,
    defaultServicingProviderCountMax,
    defaultServicingProviderCountMin,
    enableProvidersViewBackButton,
    errorLoadingContractEntity,
    errorLoadingHospital,
    errorLoadingOrganization,
    errorLoadingProvider,
    errorLoadingTopConditions,
    errorLoadingTopPrescriptions,
    errorLoadingTopProcedures,
    filtersChangedSinceLastSearch,
    filterState,
    goBackFromProfile,
    handleResetAll,
    handleSearchClick,
    hasFilteredCriteriaFailedToLoad,
    initialFilterState,
    isLoadingTopConditions,
    isLoadingTopPrescriptions,
    isLoadingTopProcedures,
    isNiMarketProviderFailed,
    isNiMarketProviderProcessing,
    isProjectReadOnly,
    isProviderHubError,
    isProviderHubMissingData,
    isProviderHubProcessing,
    isRetryingIngestion,
    isScenarioModelingError,
    isScenarioModelingProcessing,
    isSearchEnabled,
    loading,
    locationConfidenceOptions,
    marketAlternatives,
    marketAlternativesError,
    marketAlternativesLoading,
    membershipAliases,
    navigateToContractEntityProfile,
    navigateToHospitalProfile,
    navigateToOrganizationProfile,
    navigateToProviderProfile,
    networkIntegrityGhostProviderLobLabel,
    pagedAccessPoints,
    pageIndex,
    pageSize,
    pciClaimsOptions,
    pciVolumeOptions,
    providerActionStatusOptions,
    providerHubContractEntities,
    providerHubHospitals,
    providerHubOrganizations,
    providerHubServicingProviders,
    providerNetworkTypeOptions,
    referrer,
    resetToFirstPage,
    retryIngestion,
    selectedContractEntity,
    selectedHospital,
    selectedOrganization,
    selectedProvider,
    serviceAreaLoadFailed,
    serviceAreaMatchFilter,
    serviceAreaOptions,
    serviceAreaSelectionText,
    serviceAreaStatusFilters,
    setAccessPointsPage,
    setAccessPointsPageSize,
    setActiveServiceAreaStatusFilters,
    setErrorLoadingContractEntity,
    setErrorLoadingHospital,
    setErrorLoadingOrganization,
    setErrorLoadingProvider,
    setFilter,
    setMarketAlternatives,
    setMarketAlternativesError,
    setMarketAlternativesLoading,
    setPagedAccessPoints,
    setPageIndex,
    setPageSize,
    setProviderHubContractEntities,
    setProviderHubOrganizations,
    setProviderHubServicingProviders,
    setReferrer,
    setResetToFirstPage,
    setSelectedContractEntity,
    setSelectedHospital,
    setSelectedOrganization,
    setSelectedProvider,
    setServiceAreaOptions,
    setShowMembershipTypeDropdown,
    setSortColumnAndOrder,
    setSortContractEntityColumnAndOrder,
    setSpecialtyOptions,
    setTotalAccessPointsPages,
    setView,
    showAccessPointLocationConfidence,
    showAdvancedFiltersIndicator,
    showCareJourneyFilters,
    showFilterToggle,
    showLocationConfidenceFilter,
    showMembershipTypeDropdown,
    showMotiveScoreFilters,
    showNetworkIntegrityAttestationBasedAlertsFilter,
    showNetworkIntegrityCriticalAlertsFilter,
    showNetworkIntegrityFilters,
    showPciFilters,
    showTriCareFilters,
    showVaFilters,
    sortColumnAndOrder,
    sortContractEntityColumnAndOrder,
    specialtyGroupsLoadFailed,
    specialtyOptions,
    specialtySelectionText,
    statusToRegionSet,
    topConditionOptions,
    topPrescriptionOptions,
    topProcedureOptions,
    totalAccessPointsPages,
    view,
    viewNetworkExpansionOpportunityProviders,
    viewProvidersFilteredByClaimsVolume,
    viewProvidersFilteredByScore,
    viewUnderperformingProviders,
  };

  return (
    <ProviderHubContext.Provider value={contextValue}>
      {props.children}
    </ProviderHubContext.Provider>
  );
};

export { ProviderHubContext, ProviderHubContextProvider }
export type { ProviderHubContextProps };
